import React, { useState, useEffect, useCallback } from 'react';
import { productsAPI } from '../api/client';
import { Package, Plus, X, Edit2, Trash2, Save, ListTree, ChevronUp, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const emptyForm = () => ({
  name: '',
  sub_item_label: '',
  is_active: true,
  sub_items: [''],
});

const ProductCatalog = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await productsAPI.getAll();
      setProducts(res.data?.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (product) => {
    setEditingId(product.id);
    setForm({
      name: product.name || '',
      sub_item_label: product.sub_item_label || '',
      is_active: product.is_active !== 0,
      sub_items: (product.sub_items || []).map((s) => s.value),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const updateSubItem = (index, value) => {
    setForm((prev) => ({
      ...prev,
      sub_items: prev.sub_items.map((s, i) => (i === index ? value : s)),
    }));
  };

  const addSubItemRow = () =>
    setForm((prev) => ({ ...prev, sub_items: [...prev.sub_items, ''] }));

  const removeSubItemRow = (index) =>
    setForm((prev) => ({
      ...prev,
      sub_items: prev.sub_items.filter((_, i) => i !== index),
    }));

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      setError('Product name is required');
      return;
    }
    const cleanedSubItems = form.sub_items
      .map((s) => s.trim())
      .filter(Boolean)
      .map((value) => ({ value, is_active: 1 }));

    const payload = {
      name,
      sub_item_label: form.sub_item_label.trim() || null,
      is_active: form.is_active,
      sub_items: cleanedSubItems,
    };

    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await productsAPI.update(editingId, payload);
      } else {
        await productsAPI.create(payload);
      }
      closeForm();
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Remove "${product.name}" from the catalog? Existing orders are not affected.`)) {
      return;
    }
    try {
      await productsAPI.remove(product.id);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to remove product');
    }
  };

  const toggleActive = async (product) => {
    try {
      await productsAPI.update(product.id, { is_active: product.is_active ? false : true });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update product');
    }
  };

  const moveProduct = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= products.length) return;
    const reordered = [...products];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setProducts(reordered); // optimistic
    try {
      await productsAPI.reorder(reordered.map((p) => p.id));
    } catch (err) {
      setError(err.message || 'Failed to reorder products');
      await load();
    }
  };

  const moveSubItem = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= form.sub_items.length) return;
    setForm((prev) => {
      const next = [...prev.sub_items];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return { ...prev, sub_items: next };
    });
  };

  return (
    <div className="admin-page space-y-6">
      <div className="admin-page-header">
        <div className="admin-page-header-title flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20 flex-shrink-0">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-800">Product Catalog</h1>
            <p className="text-sm text-slate-500">Manage order materials and their sub-items (grades, types, sizes).</p>
          </div>
        </div>
        <div className="admin-page-header-actions">
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
        >
          <Plus className="w-4 h-4" />
          Add product
        </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center justify-between">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No products yet.</p>
          <p className="text-slate-400 text-sm">Add your first material to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              layout
              className={`bg-white border rounded-2xl p-5 space-y-4 ${product.is_active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  <div className="flex flex-col -ml-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => moveProduct(index, -1)}
                      disabled={index === 0}
                      className="p-0.5 text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveProduct(index, 1)}
                      disabled={index === products.length - 1}
                      className="p-0.5 text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="min-w-0">
                  <h3 className="font-bold text-slate-800 truncate">{product.name}</h3>
                  {product.sub_item_label && (
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <ListTree className="w-3 h-3" />
                      {product.sub_item_label}
                    </p>
                  )}
                  </div>
                </div>
                <span
                  className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full whitespace-nowrap ${
                    product.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {product.is_active ? 'Active' : 'Hidden'}
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 min-h-[1.5rem]">
                {(product.sub_items || []).length === 0 ? (
                  <span className="text-xs text-slate-400 italic">No sub-items (free-text product name)</span>
                ) : (
                  product.sub_items.map((s) => (
                    <span key={s.id} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-lg">
                      {s.value}
                    </span>
                  ))
                )}
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => openEdit(product)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => toggleActive(product)}
                  className="px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  {product.is_active ? 'Hide' : 'Show'}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(product)}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={closeForm}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white">
                <h2 className="text-lg font-bold text-slate-800">
                  {editingId ? 'Edit product' : 'Add product'}
                </h2>
                <button type="button" onClick={closeForm} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Product / Material name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. RMC, Aggregates, AAC Site Brick"
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Sub-item label</label>
                  <input
                    type="text"
                    value={form.sub_item_label}
                    onChange={(e) => setForm((prev) => ({ ...prev, sub_item_label: e.target.value }))}
                    placeholder="e.g. RMC grade, Brick Size, Aggregate type"
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                  <p className="text-xs text-slate-400 mt-1">The dropdown label shown when this material is picked in an order. Leave blank for a free-text product.</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-slate-700">Sub-items</label>
                    <button
                      type="button"
                      onClick={addSubItemRow}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {form.sub_items.length === 0 && (
                      <p className="text-xs text-slate-400 italic">No sub-items — the order will use a free-text product name.</p>
                    )}
                    {form.sub_items.map((value, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="flex flex-col flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => moveSubItem(index, -1)}
                            disabled={index === 0}
                            className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move up"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveSubItem(index, 1)}
                            disabled={index === form.sub_items.length - 1}
                            className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move down"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => updateSubItem(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => removeSubItemRow(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Active (available for new orders)</span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-100 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-60"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : editingId ? 'Save changes' : 'Create product'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductCatalog;
